import { useCallback, useEffect, useState } from "react";
import { useParams, useOutletContext, useNavigate } from "react-router-dom";
import { ArrowLeft, FileSignature } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Skeleton } from "@/components/ui/skeleton";
import { ShareholderAgreements } from "./share/ShareholderAgreements";

const CHAIR_ROLES = ["Styreleder", "Nestleder", "Daglig leder"];

export default function ShareholderAgreementPage() {
  const { companyId } = useParams();
  const { company } = useOutletContext();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [agreements, setAgreements] = useState([]);

  const load = useCallback(() => {
    Promise.all([
      api.get(`/companies/${companyId}/cap-table`),
      api.get(`/companies/${companyId}/agreements`),
    ]).then(([c, ag]) => {
      setRows(c.data?.rows || []);
      setAgreements(ag.data || []);
    });
  }, [companyId]);

  useEffect(() => { setRows(null); load(); }, [load]);

  const canEdit = !!user?.is_admin || (company?.my_roles || []).some((r) => CHAIR_ROLES.includes(r));

  return (
    <div className="mx-auto max-w-7xl">
      <button
        onClick={() => navigate(`/selskap/${companyId}/dokumenter`)}
        className="mb-4 flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
        data-testid="sh-back-to-documents"
      >
        <ArrowLeft className="h-4 w-4" /> Tilbake til Dokumenter
      </button>

      <div className="mb-8 flex items-center gap-4">
        <div className="grid h-12 w-12 place-items-center rounded-2xl bg-lime/15 text-lime">
          <FileSignature className="h-6 w-6" />
        </div>
        <div>
          <h1 className="font-headings text-3xl font-black tracking-tight">Aksjonæravtale</h1>
          <p className="text-sm text-muted-foreground">Bygg avtalen fra mal, velg klausuler og signer med BankID.</p>
        </div>
      </div>

      {rows === null ? (
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className="space-y-4 lg:col-span-8">
            <Skeleton className="h-40 rounded-2xl" />
            <Skeleton className="h-64 rounded-2xl" />
          </div>
          <div className="lg:col-span-4"><Skeleton className="h-80 rounded-2xl" /></div>
        </div>
      ) : (
        <ShareholderAgreements companyId={companyId} rows={rows} agreements={agreements} onChanged={load} canEdit={canEdit} />
      )}
    </div>
  );
}
